import type { IGradeAxisEntry, IGradeTarget } from "@/@types/campaign-grade";
import { useGradeTargetDraft } from "./use-grade-target-draft";
import { gradeTargetKey } from "../../utils/grade-targets";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  useGetGradeCatalogQuery,
  useGetItemGradeHistoryQuery,
  useValidateGradeTargetsMutation,
} from "@/services/campaign-pro014";
import { AlertCircle, AlertTriangle, Loader2, Palette, X } from "lucide-react";
import { useMemo } from "react";

import {
  ALL_COLORS_LABEL,
  ALL_SIZES_LABEL,
  BOTH_AXES_WILDCARD,
  GRADE_BLOCK_MESSAGES,
  NO_GRADE_SELECTED,
  NOTHING_TO_SAVE,
  PENDING_TARGET_DESCRIPTION,
  PENDING_TARGET_TITLE,
  SAME_TARGET_ADDED,
  VALIDATION_UNAVAILABLE,
} from "./grade-messages";

interface GradeTargetModalProps {
  isOpen: boolean;
  itemCode: string;
  itemName?: string;
  initialTargets?: IGradeTarget[];
  isSaving?: boolean;
  onConfirm: (targets: IGradeTarget[]) => void | Promise<void>;
  onClose: () => void;
}

const hasListChanged = (
  before: IGradeTarget[],
  after: IGradeTarget[],
): boolean => {
  if (before.length !== after.length) return true;
  const beforeKeys: Set<string> = new Set(before.map(gradeTargetKey));
  return after.some((target) => !beforeKeys.has(gradeTargetKey(target)));
};

export function GradeTargetModal({
  isOpen,
  itemCode,
  itemName,
  initialTargets = [],
  isSaving = false,
  onConfirm,
  onClose,
}: GradeTargetModalProps): JSX.Element {
  const { state, dispatch, currentTarget, blockMessage, warnMessage, commitTarget } =
    useGradeTargetDraft(initialTargets);
  const { targets, color, size, allColors, allSizes, confirmingPending } = state;

  const { data: catalog, isLoading: loadingCatalog } =
    useGetGradeCatalogQuery();
  const { data: history, isFetching: loadingHistory } =
    useGetItemGradeHistoryQuery(itemCode, { skip: !isOpen || !itemCode });
  const [validateTargets, { isLoading: validating }] =
    useValidateGradeTargetsMutation();

  const soldColorCodes = useMemo(
    () => new Set((history?.colors ?? []).map((entry) => entry.code)),
    [history],
  );
  const soldSizeCodes = useMemo(
    () => new Set((history?.sizes ?? []).map((entry) => entry.code)),
    [history],
  );

  const catalogOnlyColors = useMemo(
    () =>
      (catalog?.colors ?? []).filter(
        (entry) => !soldColorCodes.has(entry.code),
      ),
    [catalog, soldColorCodes],
  );
  const catalogOnlySizes = useMemo(
    () =>
      (catalog?.sizes ?? []).filter((entry) => !soldSizeCodes.has(entry.code)),
    [catalog, soldSizeCodes],
  );

  const describe = (entry: IGradeAxisEntry): string => {
    const name = entry.description ?? "fora do catálogo";
    const sales = entry.sales === 1 ? "1 venda" : `${entry.sales} vendas`;
    return `${entry.code} · ${name} · ${sales}`;
  };

  const bothWildcards: boolean = allColors && allSizes;
  const nothingChosen: boolean = !currentTarget.color && !currentTarget.size;
  const alreadyAdded: boolean = targets.some(
    (target) => gradeTargetKey(target) === gradeTargetKey(currentTarget),
  );
  const pendingTarget: boolean = !nothingChosen && !alreadyAdded;
  const hasChanges: boolean =
    hasListChanged(initialTargets, targets) || pendingTarget;

  const addPendingTarget = async (): Promise<IGradeTarget[] | null> => {
    if (bothWildcards) {
      dispatch({ type: "BLOCK", payload: BOTH_AXES_WILDCARD });
      return null;
    }
    if (nothingChosen) {
      dispatch({ type: "BLOCK", payload: NO_GRADE_SELECTED });
      return null;
    }
    if (alreadyAdded) {
      dispatch({ type: "BLOCK", payload: SAME_TARGET_ADDED });
      return null;
    }
    if (warnMessage) return commitTarget();

    const result = await validateTargets({
      targets: [{ itemCode, ...currentTarget }],
    })
      .unwrap()
      .catch(() => null);

    if (!result) {
      dispatch({ type: "BLOCK", payload: VALIDATION_UNAVAILABLE });
      return null;
    }

    const blocked = result.blocked[0];
    if (blocked) {
      dispatch({ type: "BLOCK", payload: GRADE_BLOCK_MESSAGES[blocked.reason] });
      return null;
    }

    const warning = result.warnings[0];
    if (warning) {
      dispatch({ type: "WARN", payload: GRADE_BLOCK_MESSAGES[warning.reason] });
      return null;
    }

    return commitTarget();
  };

  const handleAdd = async (): Promise<void> => {
    await addPendingTarget();
  };

  const handleSave = (): void => {
    if (pendingTarget) {
      dispatch({ type: "CONFIRM_PENDING", payload: true });
      return;
    }
    void onConfirm(targets);
  };

  const discardAndSave = (): void => {
    dispatch({ type: "CONFIRM_PENDING", payload: false });
    void onConfirm(targets);
  };

  const addAndSave = async (): Promise<void> => {
    dispatch({ type: "CONFIRM_PENDING", payload: false });
    const next: IGradeTarget[] | null = await addPendingTarget();
    if (!next) return;
    void onConfirm(next);
  };

  const removeTarget = (target: IGradeTarget): void => {
    dispatch({ type: "REMOVE_TARGET", payload: target });
  };

  const targetLabel = (target: IGradeTarget): string => {
    const colorPart = target.color ?? "todas as cores";
    const sizePart = target.size ?? "todos os tamanhos";
    return `${colorPart} / ${sizePart}`;
  };

  const isLoading: boolean = loadingCatalog || loadingHistory;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Palette className="w-5 h-5" />
            Grade específica
          </DialogTitle>
          <DialogDescription>
            {itemCode}
            {itemName ? ` — ${itemName}` : ""}
            {history && !history.hasHistory
              ? " · sem venda registrada para conferir a grade"
              : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-auto space-y-4">
          <div className="space-y-2">
            <Label htmlFor="grade-color">Cor</Label>
            <Select
              value={color}
              onValueChange={(value) => {
                dispatch({ type: "SET_COLOR", payload: value });
                dispatch({ type: "CLEAR_MESSAGE" });
              }}
              disabled={allColors || isLoading}
            >
              <SelectTrigger id="grade-color">
                <SelectValue placeholder="Selecione a cor" />
              </SelectTrigger>
              <SelectContent>
                {(history?.colors ?? []).length > 0 && (
                  <SelectGroup>
                    <SelectLabel>Deste produto</SelectLabel>
                    {(history?.colors ?? []).map((entry) => (
                      <SelectItem key={entry.code} value={entry.code}>
                        {describe(entry)}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                )}
                <SelectGroup>
                  <SelectLabel>Todo o catálogo</SelectLabel>
                  {catalogOnlyColors.map((entry) => (
                    <SelectItem key={entry.code} value={entry.code}>
                      {entry.code} · {entry.description}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="grade-size">Tamanho</Label>
            <Select
              value={size}
              onValueChange={(value) => {
                dispatch({ type: "SET_SIZE", payload: value });
                dispatch({ type: "CLEAR_MESSAGE" });
              }}
              disabled={allSizes || isLoading}
            >
              <SelectTrigger id="grade-size">
                <SelectValue placeholder="Selecione o tamanho" />
              </SelectTrigger>
              <SelectContent>
                {(history?.sizes ?? []).length > 0 && (
                  <SelectGroup>
                    <SelectLabel>Deste produto</SelectLabel>
                    {(history?.sizes ?? []).map((entry) => (
                      <SelectItem key={entry.code} value={entry.code}>
                        {describe(entry)}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                )}
                <SelectGroup>
                  <SelectLabel>Todo o catálogo</SelectLabel>
                  {catalogOnlySizes.map((entry) => (
                    <SelectItem key={entry.code} value={entry.code}>
                      {entry.code} · {entry.description}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={allColors}
                onCheckedChange={(checked) => {
                  dispatch({
                    type: "TOGGLE_ALL_COLORS",
                    payload: checked === true,
                  });
                  dispatch({ type: "CLEAR_MESSAGE" });
                }}
              />
              {ALL_COLORS_LABEL}
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={allSizes}
                onCheckedChange={(checked) => {
                  dispatch({
                    type: "TOGGLE_ALL_SIZES",
                    payload: checked === true,
                  });
                  dispatch({ type: "CLEAR_MESSAGE" });
                }}
              />
              {ALL_SIZES_LABEL}
            </label>
          </div>

          {blockMessage && (
            <p className="text-sm text-destructive flex items-start gap-1">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              {blockMessage}
            </p>
          )}

          {warnMessage && (
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md dark:bg-amber-950/20 dark:border-amber-900">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-amber-900 dark:text-amber-300">
                {warnMessage}
              </p>
            </div>
          )}

          {targets.length > 0 && (
            <div className="space-y-2">
              <Label>Grades deste produto ({targets.length})</Label>
              <div className="flex flex-wrap gap-1.5">
                {targets.map((target) => (
                  <Badge
                    key={gradeTargetKey(target)}
                    variant="secondary"
                    className="gap-1 font-mono text-xs"
                  >
                    {targetLabel(target)}
                    <button
                      type="button"
                      aria-label={`Remover grade ${targetLabel(target)}`}
                      onClick={() => removeTarget(target)}
                      className="hover:bg-destructive/20 rounded-full p-0.5"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>

        {confirmingPending && (
          <div role="alert" className="pt-4 border-t space-y-3">
            <div className="space-y-1">
              <p className="font-semibold">{PENDING_TARGET_TITLE}</p>
              <p className="text-sm text-muted-foreground">
                {PENDING_TARGET_DESCRIPTION(targetLabel(currentTarget))}
              </p>
            </div>
            <div className="flex flex-wrap justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() =>
                  dispatch({ type: "CONFIRM_PENDING", payload: false })
                }
              >
                Voltar
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={discardAndSave}
                disabled={isSaving || validating}
              >
                Descartar e salvar
              </Button>
              <Button
                type="button"
                onClick={() => void addAndSave()}
                disabled={isSaving || validating}
              >
                Adicionar e salvar
              </Button>
            </div>
          </div>
        )}

        {!confirmingPending && (
        <div className="flex justify-between gap-2 pt-4 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={() => void handleAdd()}
            disabled={validating || isLoading || Boolean(blockMessage)}
          >
            {validating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {warnMessage ? "Adicionar mesmo assim" : "Adicionar grade"}
          </Button>
          <div className="flex gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-block">
                    <Button
                      type="button"
                      disabled={isSaving || validating || !hasChanges}
                      onClick={handleSave}
                    >
                      {isSaving && (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      )}
                      Salvar
                    </Button>
                  </span>
                </TooltipTrigger>
                {!hasChanges && (
                  <TooltipContent>{NOTHING_TO_SAVE}</TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
        )}

      </DialogContent>
    </Dialog>
  );
}
